// src/modals/CSATDetailsModal.jsx
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import BaseExpandedCard from '../components/cards/BaseExpandedCard';
import { TrendIndicator, LoadingState, ErrorState, EmptyState, StatCard } from '../components/common';
import { useModalData } from '../hooks/useModalData';
import { getCsatColor } from '../utils/colorHelpers';
import { formatDate } from '../utils/formatters';
import { API_BASE_URL } from '../config';
import { TEXT_SECONDARY } from '../utils/colors';

// ── helpers ─────────────────────────────────────────────────────────────────

const getRatingLabel = (avg) => {
  if (avg === null || avg === undefined) return '—';
  if (avg >= 4.5) return 'Excellent';
  if (avg >= 4.0) return 'Very Good';
  if (avg >= 3.5) return 'Good';
  if (avg >= 3.0) return 'Average';
  if (avg >= 2.0) return 'Below Average';
  return 'Poor';
};

// Group scores by project and compute per-engagement average
const groupByEngagement = (scores) => {
  if (!scores?.length) return [];
  const map = {};
  scores.forEach((s) => {
    const key = s.project_id || s.project || s.client;
    if (!map[key]) {
      map[key] = {
        client: s.client || '—',
        engagement: s.project || '—',
        role: s.role || null,
        project_status: s.project_status || '—',
        latestDate: s.date,
        latestScore: s.score,
        scores: [],
      };
    }
    const entry = map[key];
    entry.scores.push(s.score);
    // keep the most recent date / score
    if (s.date && (!entry.latestDate || new Date(s.date) > new Date(entry.latestDate))) {
      entry.latestDate = s.date;
      entry.latestScore = s.score;
    }
  });
  return Object.values(map).map((e) => ({
    ...e,
    avgScore: e.scores.reduce((a, b) => a + b, 0) / e.scores.length,
  }));
};

// ── sub-components ───────────────────────────────────────────────────────────

const ScoreBar = ({ score, max = 5 }) => {
  const pct = Math.min((score / max) * 100, 100);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, color: getCsatColor(score), minWidth: 28 }}>
        {score.toFixed(1)}
      </Typography>
      <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.08)' }}>
        <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: 3, bgcolor: getCsatColor(score) }} />
      </Box>
    </Box>
  );
};

// ── main component ───────────────────────────────────────────────────────────

const CSATDetailsModal = ({ open, onClose, employeeName, employee_id, tabMode = false }) => {
  const { data: csatData, loading, error } = useModalData(
    `${API_BASE_URL}/csat/employee/${employee_id}`,
    open,
    { cache: true, ttl: 5 * 60 * 1000 }
  );

  const displayData = useMemo(() => csatData || {
    average: null, count: 0, trend: 'neutral', scores: [], last_updated: null,
  }, [csatData]);

  const highest = useMemo(() => {
    if (!displayData.scores?.length) return null;
    return Math.max(...displayData.scores.map((s) => s.score ?? 0));
  }, [displayData.scores]);

  const engagements = useMemo(() => groupByEngagement(displayData.scores), [displayData.scores]);

  const hasRole = engagements.some((e) => e.role);

  const Wrap = ({ children }) => tabMode
    ? <>{children}</>
    : (
      <BaseExpandedCard open={open} onClose={onClose} title={`CSAT Details: ${employeeName}`} subtitle={`Client Satisfaction Scores (${displayData.count || 0} measurements)`}>
        {children}
      </BaseExpandedCard>
    );

  if (loading) return <Wrap><LoadingState message="Fetching CSAT data..." /></Wrap>;
  if (error)   return <Wrap><ErrorState message={error} /></Wrap>;

  const avg = displayData.average;

  return (
    <Wrap>
      {/* ── Section heading ─────────────────────────────────────── */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
        Customer Satisfaction (CSAT)
      </Typography>

      {/* ── Stat cards ──────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <StatCard label="Average CSAT">
          {avg !== null && avg !== undefined ? (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: getCsatColor(avg), lineHeight: 1 }}>
                {avg.toFixed(1)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>/ 5.0</Typography>
            </Box>
          ) : (
            <Typography variant="h5" sx={{ fontWeight: 700 }}>N/A</Typography>
          )}
        </StatCard>

        <StatCard label="Rating">
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            {getRatingLabel(avg)}
          </Typography>
        </StatCard>

        <StatCard label="Engagements Rated">
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
            {displayData.count || 0}
          </Typography>
        </StatCard>

        <StatCard label="Highest">
          <Typography variant="h5" sx={{ fontWeight: 700, color: highest !== null ? getCsatColor(highest) : 'text.primary', lineHeight: 1 }}>
            {highest !== null ? highest.toFixed(1) : '—'}
          </Typography>
        </StatCard>

        <StatCard label="Trend">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, textTransform: 'capitalize', lineHeight: 1.2 }}>
              {displayData.trend === 'neutral' ? 'Stable' : (displayData.trend?.charAt(0).toUpperCase() + displayData.trend?.slice(1)) || '—'}
            </Typography>
            <TrendIndicator trend={displayData.trend} />
          </Box>
        </StatCard>
      </Box>

      {/* ── Engagement table ─────────────────────────────────────── */}
      {engagements.length > 0 ? (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary', letterSpacing: '0.03em' }}>
            CSAT by Engagement
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& .MuiTableCell-head': { color: TEXT_SECONDARY, fontSize: '0.65rem', letterSpacing: '0.06em', textTransform: 'uppercase', pb: 1 } }}>
                  <TableCell>Client</TableCell>
                  <TableCell>Engagement</TableCell>
                  {hasRole && <TableCell>Role</TableCell>}
                  <TableCell>Status</TableCell>
                  <TableCell>Last Measured</TableCell>
                  <TableCell sx={{ minWidth: 140 }}>Latest Score</TableCell>
                  <TableCell align="right">Avg Score</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {engagements.map((row, i) => (
                  <TableRow key={i} sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem' }}>{row.client}</TableCell>
                    <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem' }}>{row.engagement}</TableCell>
                    {hasRole && <TableCell sx={{ color: 'text.primary', fontSize: '0.8rem' }}>{row.role || '—'}</TableCell>}
                    <TableCell sx={{ color: TEXT_SECONDARY, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{row.project_status}</TableCell>
                    <TableCell sx={{ color: TEXT_SECONDARY, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{formatDate(row.latestDate)}</TableCell>
                    <TableCell>
                      <ScoreBar score={row.latestScore} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: getCsatColor(row.avgScore), fontSize: '0.85rem' }}>
                      {row.avgScore.toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <EmptyState
          title="No CSAT data available"
          message="This employee has no recorded client satisfaction scores"
        />
      )}

      {/* ── Footer ───────────────────────────────────────────────── */}
      {displayData.last_updated && (
        <Typography variant="caption" sx={{ display: 'block', mt: 2, color: TEXT_SECONDARY }}>
          Last updated: {formatDate(displayData.last_updated)}
        </Typography>
      )}
    </Wrap>
  );
};

CSATDetailsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  employeeName: PropTypes.string.isRequired,
  employee_id: PropTypes.string.isRequired,
  tabMode: PropTypes.bool,
};

export default CSATDetailsModal;
